/**
 * The desktop preload bridge (`window.desktop`), as the web app sees it.
 *
 * Declared structurally rather than imported from `apps/desktop`: the web app
 * has to build and run without the desktop package present, since the very same
 * build is the hosted deployment (where `window.desktop` simply does not
 * exist). These shapes must stay in step with `apps/desktop/src/types.ts` —
 * `printService.ts` already keeps its own slice of this contract for the same
 * reason, and the wizard/settings tests exercise the parts used here against a
 * fake that matches the real bridge.
 */

export const DEFAULT_LAN_PORT = 8477;

export type DeploymentRole = "host" | "client";

export interface DesktopSettings {
  setupComplete: boolean;
  role: DeploymentRole;
  dbDir: string;
  printerName: string | null;
  lan: { enabled: boolean; passphrase: string | null };
  remote: { host: string | null; port: number; passphrase: string | null };
}

export type DesktopSettingsPatch = Partial<Omit<DesktopSettings, "lan" | "remote">> & {
  lan?: Partial<DesktopSettings["lan"]>;
  remote?: Partial<DesktopSettings["remote"]>;
};

export interface PrinterInfo {
  name: string;
  displayName: string;
  description: string;
  isDefault: boolean;
}

export interface ServerStatus {
  localUrl: string;
  lanUrls: string[];
  port: number;
  bound: "loopback" | "lan";
}

/** Which legacy dump the file picker is choosing — mirrors `apps/desktop/src/types.ts`. */
export type LegacyImportFileKind = "sources" | "employees";

export interface LegacyImportRequest {
  sources: readonly string[];
  employeesPath?: string | null;
}

export interface LegacyImportSummary {
  sources: readonly { file: string; rowsRead: number }[];
  totalMergedRecords: number;
  imported: number;
  droppedEmpty: number;
  quarantined: number;
  employees: number;
  seasons: readonly { label: string; count: number }[];
  fieldDisagreements: number;
  numberCollisions: number;
}

export type LegacyImportResult =
  | { ok: true; summary: LegacyImportSummary }
  | { ok: false; message: string };

export interface DesktopBridge {
  settings: {
    get(): Promise<DesktopSettings>;
    update(patch: DesktopSettingsPatch): Promise<DesktopSettings>;
  };
  printers: {
    list(): Promise<PrinterInfo[]>;
  };
  server: {
    status(): Promise<ServerStatus>;
  };
  legacyImport: {
    pickFiles(kind: LegacyImportFileKind): Promise<string[]>;
    run(request: LegacyImportRequest): Promise<LegacyImportResult>;
  };
}

/**
 * The live bridge, or `null` when the SPA is not running inside the desktop
 * shell (the hosted deployment, or a plain browser pointed at a desktop host
 * over the LAN — that page is served by the host but has no preload of its
 * own). Callers must handle `null`: everything the bridge exposes is
 * desktop-only.
 */
export function getDesktopBridge(): DesktopBridge | null {
  const bridge = (globalThis as { desktop?: DesktopBridge }).desktop;
  return bridge && typeof bridge.settings?.get === "function" ? bridge : null;
}
